import { MovementTypes } from "constants/index";
import { dateToMySQLFormat } from "helpers";
import {
  AdditionalItem,
  Client,
  Movement,
  MovementAdditionalItemDraft,
} from "types";

export type RequiredAdditionalMovementType = "income" | "discharge";

const getMovementTypeLabel = (movementTypeId: number) => {
  switch (movementTypeId) {
    case MovementTypes.Income:
      return "Ingreso";

    case MovementTypes.Discharge:
      return "Egreso";

    case MovementTypes.Preparation:
      return "Pedido";

    default:
      return "Movimiento";
  }
};

export const getOriginMovementLabel = (movement: Movement) => {
  const movementType = getMovementTypeLabel(movement.movement_type_id);

  return movement.id ? `${movementType} #${movement.id}` : `${movementType} Pendiente`;
};

export const getAdditionalItemsModalTitle = (
  movement: Movement,
  includeOrigin: boolean = false,
) => {
  const movementLabel = includeOrigin
    ? getOriginMovementLabel(movement)
    : getMovementTypeLabel(movement.movement_type_id);

  return `Ítems adicionales asociados al ${movementLabel.toLowerCase()}`;
};

export const getAdditionalItemPriceForClient = (
  additionalItem: AdditionalItem | null,
  client: Client | null,
) => {
  if (!additionalItem) return "";

  const customPrice = client?.client_additional_items?.find(
    (clientAdditionalItem) =>
      clientAdditionalItem.additional_item_id === additionalItem.id,
  )?.price;

  return customPrice ?? additionalItem.price;
};

const getMovementArticleClientIds = (movement: Movement) => {
  const articleClientIds = movement.articles
    .map((article) => article.client_id)
    .filter((clientId): clientId is number => Boolean(clientId));

  return Array.from(new Set(articleClientIds));
};

export const getMissingRequiredAdditionalItems = ({
  movement,
  clients,
  additionalItems,
  selectedItems,
  movementType,
}: {
  movement: Movement;
  clients: Client[];
  additionalItems: AdditionalItem[];
  selectedItems: MovementAdditionalItemDraft[];
  movementType: RequiredAdditionalMovementType;
}) => {
  const movementLineClientIds = getMovementArticleClientIds(movement);
  const lineClientIds =
    movementLineClientIds.length > 0
      ? movementLineClientIds
      : [movement.client_id];

  const selectedKeys = new Set(
    selectedItems.map(
      (item) => `${item.line_client_id}-${item.additional_item_id}`,
    ),
  );

  return lineClientIds.flatMap((lineClientId) => {
    const lineClient = clients.find((client) => client.id === lineClientId);

    if (!lineClient) return [];

    return (lineClient.client_additional_items ?? [])
      .filter((item) => item.is_required === true)
      .filter((item) =>
        (item.required_movement_types ?? []).includes(movementType),
      )
      .filter(
        (item) =>
          !selectedKeys.has(`${lineClient.id}-${item.additional_item_id}`),
      )
      .map((item) => {
        const additionalItem = additionalItems.find(
          (additional) => additional.id === item.additional_item_id,
        );

        if (!additionalItem) return null;

        return {
          additionalItem,
          lineClient,
        };
      })
      .filter(
        (
          item,
        ): item is {
          additionalItem: AdditionalItem;
          lineClient: Client;
        } => item !== null,
      );
  });
};

export const buildRequiredAdditionalItemDrafts = (
  missingItems: ReturnType<typeof getMissingRequiredAdditionalItems>,
) =>
  missingItems.map(({ additionalItem, lineClient }, index) => ({
    id: `required-${lineClient.id}-${additionalItem.id}-${index}`,
    additional_item_id: additionalItem.id,
    description: additionalItem.description,
    amount: 1,
    price: Number(getAdditionalItemPriceForClient(additionalItem, lineClient)),
    client_id: additionalItem.client_id,
    line_client_id: lineClient.id,
    line_client_name: lineClient.razon_social,
    additional_item: additionalItem,
  }));

export const buildAdditionalMovementsPayload = ({
  baseMovement,
  headerMovement,
  items,
  originMovementId,
}: {
  baseMovement: Movement;
  headerMovement?: Movement;
  items: MovementAdditionalItemDraft[];
  originMovementId: number;
}) => {
  const additionalHeader = headerMovement ?? baseMovement;

  const baseMovementPayload = {
    movement_type_id: baseMovement.movement_type_id,
    date: baseMovement.date,
    client_id: baseMovement.client_id,
    associated_client: baseMovement.associated_client,
    client_document: baseMovement.client_document,
    numero_remito: baseMovement.numero_remito,
    pallets: baseMovement.pallets,
    observation: baseMovement.observation,
    created_by: baseMovement.created_by,
    updated_by: baseMovement.updated_by,
    is_billable: baseMovement.is_billable,
    store_id: baseMovement.store_id,
    weight: baseMovement.weight,
    show_lote: baseMovement.show_lote,
    show_fecha_vencimiento: baseMovement.show_fecha_vencimiento,
    show_oc: baseMovement.show_oc,
  };

  const headerMovementPayload = {
    date: additionalHeader.date,
    client_id: additionalHeader.client_id,
    associated_client: additionalHeader.associated_client,
    client_document: additionalHeader.client_document,
    numero_remito: additionalHeader.numero_remito,
    pallets: additionalHeader.pallets,
    observation: additionalHeader.observation,
    store_id: additionalHeader.store_id,
    weight: additionalHeader.weight,
    show_lote: additionalHeader.show_lote,
    show_fecha_vencimiento: additionalHeader.show_fecha_vencimiento,
    show_oc: additionalHeader.show_oc,
  };

  const groupedAdditionalItems = items.reduce(
    (acc, item) => {
      if (!acc[item.line_client_id]) {
        acc[item.line_client_id] = [];
      }

      acc[item.line_client_id].push(item);

      return acc;
    },
    {} as Record<number, MovementAdditionalItemDraft[]>,
  );

  const normalizedDate = additionalHeader.date
    ? dateToMySQLFormat(additionalHeader.date)
    : "";

  return Object.entries(groupedAdditionalItems).map(([lineClientId, groupedItems]) => ({
    ...baseMovementPayload,
    ...headerMovementPayload,
    date: normalizedDate,
    movement_type_id: MovementTypes.Additional,
    movement_id: originMovementId,
    client_id: Number(lineClientId),
    store_id: null,
    articles: [],
    containers: [],
    additional_items: groupedItems.map(
      ({ line_client_id, line_client_name, ...item }) => item,
    ),
  }));
};
